<script>
import { Log } from '@artup/utilities'
import { Countries } from '@artup/model'
import ViewMixin from '../home/views/view'

export default {
  mixins: [
    ViewMixin
  ],

  inject: {
    router: { from: 'routerStore' }
  },

  data () {
    return {
      // Indicates that the accounts are being loaded
      isLoading: false,
      // Error which occurred while loading the accounts
      error: null,
      // Text to filter the listing with
      filter: '',
      // Table pagination
      pagination: {
        sortBy: 'created',
        descending: true,
        rowsPerPage: 25
      },
      // Table columns
      columns: [
        { name: 'fullName', label: 'Name', field: row => row.fullName, align: 'left', sortable: true },
        { name: 'created', label: 'Registered', field: 'created', align: 'left', sortable: true },
        { name: 'securityProfile', label: 'Security profile', field: row => this.profileLabel(row.avatar?.securityProfileName), align: 'left', sortable: true },
        { name: 'name', label: 'E-mail', field: 'name', align: 'left', sortable: true },
        { name: 'country', label: 'Country', field: row => this.countryName(row.countryCode), align: 'left', sortable: true },
        { name: 'isVerified', label: 'Status', field: 'isVerified', align: 'left', sortable: true }
      ]
    }
  },

  computed: {
    // All accounts except the integration accounts, which are technical and managed manually
    allAccounts () {
      return (this.accounts.accounts || []).filter(account => !account.isIntegrationAccount)
    },

    // Security profiles by name, for user-friendly labels
    securityProfiles () {
      return (this.accounts.securityProfiles || []).reduce((all, profile) => ({ ...all, [profile.name]: profile }), {})
    }
  },

  methods: {
    // Loads the accounts
    async populate () {
      this.isLoading = true
      this.error = null

      try {
        await Promise.all([
          this.accounts.loadAccounts(),
          this.accounts.loadSecurityProfiles()
        ])
      } catch (error) {
        Log.exception(error)
        this.error = error
      } finally {
        this.isLoading = false
      }
    },

    // Opens the editor of the specified account
    edit (account) {
      this.router.gotoRoute({ name: 'account-editor', params: { id: account.id } })
    },

    // Returns the user-friendly label of a security profile
    profileLabel (name) {
      return this.securityProfiles[name]?.description || name || ''
    },

    // Returns the name of a country
    countryName (code) {
      if (!code) return ''
      const country = Countries.All.find(country => country.code.toLowerCase() === code.toLowerCase())
      return country?.name || code.toUpperCase()
    },

    // Returns the color representing a profile
    profileColor (profile) {
      switch (profile) {
        case 'super':
          return 'purple-7'
        case 'administrator':
          return 'indigo-7'
        default:
          return 'grey-7'
      }
    }
  },

  async created () {
    await this.populate()
  }
}
</script>

<template>
  <main class="artup-account-view all-accounts">
    <header class="row items-center q-mb-lg">
      <span class="text-h5">
        All Accounts
      </span>
      <q-space></q-space>
      <q-input v-model="filter" dense outlined debounce="300" placeholder="Filter" class="filter">
        <template v-slot:append>
          <q-icon name="search"></q-icon>
        </template>
      </q-input>
      <q-btn icon="refresh" flat round dense color="grey-7" class="q-ml-sm" :loading="isLoading"
        @click="populate()">
        <q-tooltip>
          Refresh
        </q-tooltip>
      </q-btn>
    </header>

    <q-table flat bordered dense row-key="id" :rows="allAccounts" :columns="columns" :loading="isLoading"
      :filter="filter" v-model:pagination="pagination" :rows-per-page-options="[25, 50, 100, 0]"
      no-data-label="No accounts yet" @row-click="(_, row) => edit(row)">


      <template v-slot:body-cell-created="props">
        <q-td :props="props">
          {{ date(props.value) }}
        </q-td>
      </template>

      <template v-slot:body-cell-securityProfile="props">
        <q-td :props="props">
          <q-badge v-if="props.value" :color="profileColor(props.row.avatar?.securityProfileName)" :label="props.value">
          </q-badge>
        </q-td>
      </template>

      <template v-slot:body-cell-isVerified="props">
        <q-td :props="props">
          <q-badge v-if="props.value" color="green-7" label="verified">
          </q-badge>
          <q-badge v-else color="orange-7" label="not verified">
          </q-badge>
          <q-badge v-if="!props.row.isEnabled" color="red-7" label="disabled" class="q-ml-xs">
          </q-badge>
        </q-td>
      </template>
    </q-table>

    <div v-if="error" class="text-negative q-mt-md">
      Accounts could not be loaded. {{ error.message }}
    </div>
  </main>
</template>

<style scoped lang="scss">
@import "../home/views/view.scss";

.filter {
  width: 220px;
}

:deep(tbody tr) {
  cursor: pointer;
}

</style>
